/* @jsx h */
import { defineComponent, h } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import type { VacationSchedule } from "@lemmonhq/types";

interface VacationDayFormProps {
  item: VacationSchedule["items"][0];
  onUpdateField: (field: "date" | "reason" | "guestNote", value: string) => void;
  onToggleDayType: () => void;
  onToggleService: (
    service: "dine-in" | "takeaway" | "delivery",
    intervalIndex?: number,
  ) => void;
  onAddInterval: () => void;
  onRemoveInterval: (intervalIndex: number) => void;
  onUpdateInterval: (
    intervalIndex: number,
    field: "start" | "end",
    value: string,
  ) => void;
}

export const VacationDayForm = defineComponent<VacationDayFormProps>({
  name: "VacationDayForm",
  props: [
    "item",
    "onUpdateField",
    "onToggleDayType",
    "onToggleService",
    "onAddInterval",
    "onRemoveInterval",
    "onUpdateInterval",
  ],
  setup(props) {
    const { t } = useLocalization();

    const ServiceToggle = ({
      service,
      enabled,
      onClick,
    }: {
      service: string;
      enabled: boolean;
      onClick: () => void;
    }) => (
      <button
        type="button"
        onClick={onClick}
        class={[
          "px-3 py-1.5 rounded-full text-xs font-medium transition-all",
          enabled
            ? "bg-primary text-primary-content shadow-sm"
            : "bg-base-200 text-base-content/60 hover:bg-base-300",
        ].join(" ")}
      >
        {service}
      </button>
    );

    return () => (
      <div class="bg-base-200/70">
        <div class="p-6 xl:p-8 space-y-6">
            {/* Date & Reason Card */}
            <div class="card bg-base-100 shadow-lg border ">
              <div class="card-body p-6 space-y-4">
                <div class="flex items-center gap-2">
                  <span class="material-symbols-outlined text-primary">
                    calendar_today
                  </span>
                  <h3 class="text-lg font-semibold text-accent">
                    {t("settings.vacation.date_details")}
                  </h3>
                </div>

                <div class="space-y-4">
                  <div>
                    <label class="label">
                      <span class="label-text font-medium">
                        {t("settings.vacation.date")}
                      </span>
                      <span class="label-text-alt text-error">
                        {t("settings.vacation.required")}
                      </span>
                    </label>
                    <input
                      type="date"
                      value={props.item.date}
                      class="input input-bordered w-full focus:input-primary focus:ring-2 focus:ring-primary/20 transition-colors"
                      onInput={(e) =>
                        props.onUpdateField(
                          "date",
                          (e.target as HTMLInputElement).value,
                        )
                      }
                    />
                  </div>

                  <div>
                    <label class="label">
                      <span class="label-text font-medium">
                        {t("settings.vacation.reason_optional")}
                      </span>
                    </label>
                    <input
                      type="text"
                      value={props.item.reason || ""}
                      placeholder={t("settings.vacation.reason_placeholder")}
                      class="input input-bordered w-full focus:input-primary focus:ring-2 focus:ring-primary/20 transition-colors"
                      onInput={(e) =>
                        props.onUpdateField(
                          "reason",
                          (e.target as HTMLInputElement).value,
                        )
                      }
                    />
                    <label class="label">
                      <span class="label-text-alt text-base-content/60">
                        {t("settings.vacation.reason_hint")}
                      </span>
                    </label>
                  </div>

                  <div>
                    <label class="label">
                      <span class="label-text font-medium">
                        {t("settings.vacation.guest_note_label")}
                      </span>
                    </label>
                    <input
                      type="text"
                      value={props.item.guestNote || ""}
                      placeholder={t(
                        "settings.vacation.guest_note_placeholder",
                      )}
                      class="input input-bordered w-full focus:input-primary focus:ring-2 focus:ring-primary/20 transition-colors"
                      onInput={(e) =>
                        props.onUpdateField(
                          "guestNote",
                          (e.target as HTMLInputElement).value,
                        )
                      }
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Status Card */}
            <div class="card bg-base-100 shadow-lg border ">
              <div class="card-body p-6 space-y-4">
                <div class="flex items-center gap-2">
                  <span class="material-symbols-outlined text-primary">
                    {props.item.payload.type === "closed_all_day"
                      ? "event_busy"
                      : "schedule"}
                  </span>
                  <h3 class="text-lg font-semibold text-accent">
                    {t("settings.vacation.status")}
                  </h3>
                </div>

                <button
                  type="button"
                  onClick={props.onToggleDayType}
                  class={[
                    "btn gap-2 w-full justify-start text-left h-auto py-4",
                    props.item.payload.type === "closed_all_day"
                      ? "btn-error"
                      : "btn-info",
                  ].join(" ")}
                >
                  <span class="material-symbols-outlined text-2xl">
                    {props.item.payload.type === "closed_all_day"
                      ? "close"
                      : "schedule"}
                  </span>
                  <div class="flex-1">
                    <div class="font-semibold text-base">
                      {props.item.payload.type === "closed_all_day"
                        ? t("settings.vacation.closed_all_day")
                        : t("settings.vacation.custom_hours")}
                    </div>
                    <div class="text-xs opacity-80 font-normal mt-0.5">
                      {props.item.payload.type === "closed_all_day"
                        ? t("settings.vacation.closed_desc")
                        : t("settings.vacation.custom_desc")}
                    </div>
                  </div>
                  <span class="material-symbols-outlined">swap_horiz</span>
                </button>
              </div>
            </div>

            {/* Services or Intervals */}
            {props.item.payload.type === "intervals" && (
              <div class="card bg-base-100 shadow-lg border ">
                <div class="card-body p-6 space-y-4">
                  <div class="flex items-center justify-between">
                    <div class="flex items-center gap-2">
                      <span class="material-symbols-outlined text-primary">
                        schedule
                      </span>
                      <h3 class="text-lg font-semibold text-accent">
                        {t("settings.vacation.operating_hours")}
                      </h3>
                    </div>
                    <button
                      type="button"
                      onClick={props.onAddInterval}
                      class="btn btn-ghost btn-sm gap-1"
                    >
                      <span class="material-symbols-outlined text-base">
                        add
                      </span>
                      {t("settings.vacation.add_interval")}
                    </button>
                  </div>

                  <p class="text-sm text-base-content/70">
                    {t("settings.vacation.intervals_desc")}
                  </p>

                  <div class="space-y-3">
                    {props.item.payload.type === "intervals" &&
                      props.item.payload.intervals.map(
                        (interval, intervalIndex) => (
                          <div
                            key={intervalIndex}
                            class="border-2  rounded-xl p-4 space-y-3 hover:border-primary/30 transition-colors"
                          >
                            <div class="flex items-center gap-3">
                              <div class="flex-1 grid grid-cols-[1fr_auto_1fr] gap-3 items-center">
                                <div>
                                  <label class="label py-0 pb-1">
                                    <span class="label-text-alt">
                                      {t("settings.vacation.start")}
                                    </span>
                                  </label>
                                  <input
                                    type="time"
                                    value={interval.start}
                                    class="input input-bordered input-sm w-full focus:input-primary"
                                    onInput={(e) =>
                                      props.onUpdateInterval(
                                        intervalIndex,
                                        "start",
                                        (e.target as HTMLInputElement).value,
                                      )
                                    }
                                  />
                                </div>
                                <span class="text-base-content/40 pt-6">
                                  {t("settings.vacation.to")}
                                </span>
                                <div>
                                  <label class="label py-0 pb-1">
                                    <span class="label-text-alt">
                                      {t("settings.vacation.end")}
                                    </span>
                                  </label>
                                  <input
                                    type="time"
                                    value={interval.end}
                                    class="input input-bordered input-sm w-full focus:input-primary"
                                    onInput={(e) =>
                                      props.onUpdateInterval(
                                        intervalIndex,
                                        "end",
                                        (e.target as HTMLInputElement).value,
                                      )
                                    }
                                  />
                                </div>
                              </div>
                              {props.item.payload.type === "intervals" &&
                                props.item.payload.intervals.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      props.onRemoveInterval(intervalIndex)
                                    }
                                    class="btn btn-ghost btn-sm btn-square"
                                  >
                                    <span class="material-symbols-outlined text-error">
                                      delete
                                    </span>
                                  </button>
                                )}
                            </div>

                            <div>
                              <label class="label py-0 pb-2">
                                <span class="label-text-alt">
                                  {t("settings.vacation.available_services")}
                                </span>
                              </label>
                              <div class="flex flex-wrap gap-2">
                                <ServiceToggle
                                  service={t("settings.vacation.dine_in")}
                                  enabled={!!interval.services?.["dine-in"]}
                                  onClick={() =>
                                    props.onToggleService(
                                      "dine-in",
                                      intervalIndex,
                                    )
                                  }
                                />
                                <ServiceToggle
                                  service={t("settings.vacation.takeaway")}
                                  enabled={!!interval.services?.["takeaway"]}
                                  onClick={() =>
                                    props.onToggleService(
                                      "takeaway",
                                      intervalIndex,
                                    )
                                  }
                                />
                                <ServiceToggle
                                  service={t("settings.vacation.delivery")}
                                  enabled={!!interval.services?.["delivery"]}
                                  onClick={() =>
                                    props.onToggleService(
                                      "delivery",
                                      intervalIndex,
                                    )
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        ),
                      )}
                  </div>
                </div>
              </div>
            )}

            {/* Info Alert */}
            <div class="alert alert-info">
              <span class="material-symbols-outlined">info</span>
              <div class="flex-1">
                <p class="text-sm">{t("settings.vacation.save_info")}</p>
              </div>
            </div>
          </div>
        </div>
    );
  },
});
